export function getDisplayLabel(dropDownArray: any[], value: string): string {
  const item = dropDownArray.find(item => item.value === value);
  return item ? item.label : value;
}


interface DownloadOptions {
  filename?: string
  includeStyles?: boolean
  excludeSelectors?: string[]
}

/**
 * Downloads the content of a specific element as HTML
 */
export const downloadElementAsHTML = (
  elementId: string,
  options: DownloadOptions = {}
) => {
  const {
    filename = 'dashboard-export.html',
    includeStyles = true,
    excludeSelectors = []
  } = options

  // Get the target element
  const element = document.getElementById(elementId)
  if (!element) {
    console.error(`Element with ID "${elementId}" not found`)
    return
  }

  // Clone the element to avoid modifying the original
  const clonedElement = element.cloneNode(true) as HTMLElement

  // Remove excluded elements
  excludeSelectors.forEach(selector => {
    const elementsToRemove = clonedElement.querySelectorAll(selector)
    elementsToRemove.forEach(el => el.remove())
  })

  // Get all stylesheets
  let stylesHTML = ''
  if (includeStyles) {
    // Include existing stylesheets (now PDF-safe thanks to globals.css fixes)
    const styleSheets = Array.from(document.styleSheets)
    stylesHTML = styleSheets.map(sheet => {
      try {
        const rules = Array.from(sheet.cssRules || sheet.rules || [])
        return rules.map(rule => rule.cssText).join('\n')
      } catch {
        // Cross-origin stylesheets may throw errors
        return ''
      }
    }).join('\n')

    // Add minimal PDF optimizations
    stylesHTML += `
			<style>
				/* PDF-specific optimizations */
				[data-exclude-from-export="true"] {
					display: none !important;
				}
				
				/* Force proper text colors for export */
				body {
					background: white !important;
					color: #1f2937 !important; /* Dark gray for readability */
				}
				
				/* Ensure all text is dark and readable */
				*, *::before, *::after {
					color: inherit !important;
				}
				
				/* Override light text colors */
				.text-gray-400, .text-gray-500 {
					color: #6b7280 !important;
				}
				
				/* Force proper dark mode table colors */
				table {
					background: inherit !important;
					table-layout: fixed !important;
					width: 100% !important;
					word-wrap: break-word !important;
					font-size: 11px !important;
				}

				/* Fix table cell overflow for PDF */
				td, th {
					word-wrap: break-word !important;
					word-break: break-word !important;
					overflow-wrap: break-word !important;
					hyphens: auto !important;
					white-space: normal !important;
					max-width: 0 !important;
					padding: 8px 4px !important;
				}

				/* Responsive table wrapper for PDF */
				.overflow-x-auto {
					overflow: visible !important;
				}
				
				.bg-gray-800 {
					background-color: #1f2937 !important;
					color: #d1d5db !important;
				}
				
				.bg-gray-700 {
					background-color: #374151 !important;
					color: #d1d5db !important;
				}
				
				.text-gray-300 {
					color: #d1d5db !important;
				}
				
				/* Ensure table cells in dark mode are visible */
				table.bg-gray-800 td,
				table.bg-gray-800 th {
					color: #d1d5db !important;
				}
				
				tbody.bg-gray-800 td {
					color: #d1d5db !important;
					background-color: #1f2937 !important;
				}

				/* Make all cards full width in HTML export */
				.grid {
					display: grid !important;
					grid-template-columns: 1fr !important;
				}

				.grid > * {
					width: 100% !important;
					max-width: 100% !important;
				}

				.lg\\:grid-cols-2 {
					grid-template-columns: 1fr !important;
				}
				
				/* Print-specific styles */
				@media print {
					body { 
						background: white !important; 
						color: black !important;
					}
					.shadow-2xl { box-shadow: none !important; }
					
					/* Force table to fit page width in print */
					table {
						font-size: 9px !important;
						table-layout: fixed !important;
						page-break-inside: avoid !important;
					}
					
					td, th {
						padding: 6px 3px !important;
						font-size: 9px !important;
					}
					
					/* Break long tables across pages */
					tbody tr {
						page-break-inside: avoid !important;
					}

					/* Make all cards full width in PDF/print */
					.grid {
						display: grid !important;
						grid-template-columns: 1fr !important;
					}

					.grid > * {
						width: 100% !important;
						max-width: 100% !important;
					}

					.lg\\:grid-cols-2 {
						grid-template-columns: 1fr !important;
					}
				}
			</style>
		`
  }

  // Create the complete HTML document
  const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
	<meta charset="UTF-8">
	<meta name="viewport" content="width=device-width, initial-scale=1.0">
	<title>Dashboard Export - ${new Date().toLocaleDateString()}</title>
  <link
      href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500&display=swap"
      rel="stylesheet"
    />
    <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
    <link
      rel="stylesheet"
      href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css"
    />
	${includeStyles ? `<style>${stylesHTML}</style>` : ''}
</head>
<body>
	<div class="export-wrapper" style="font-family: system-ui, -apple-system, sans-serif;">
		<header style="border-bottom: 1px solid #e5e7eb; padding: 1rem; margin-bottom: 2rem;">
			<h1 style="font-size: 1.5rem; font-weight: bold; margin: 0;">RADAR Dashboard Export</h1>
			<p style="color: #6b7280; margin: 0.5rem 0 0 0;">Generated on ${new Date().toLocaleString()}</p>
		</header>
		${clonedElement.outerHTML}
	</div>
</body>
</html>
	`.trim()

  // Create blob and download
  const blob = new Blob([htmlContent], { type: 'text/html' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Clean up
  URL.revokeObjectURL(url)
}

/**
 * Downloads the entire dashboard content as HTML
 */
export const downloadDashboard = (organizationName?: string, pan?: string) => {
  const timestamp = new Date().toISOString().slice(0, 10)
  const orgName = organizationName?.replace(/[^a-zA-Z0-9]/g, '_') || 'Dashboard'
  const filename = `${orgName}_${pan || 'Export'}_${timestamp}.html`

  downloadElementAsHTML('ngo-diligence-content', {
    filename,
    includeStyles: true,
    excludeSelectors: [
      '.download-button',
      '[data-exclude-from-export="true"]'
    ]
  })
}

/**
 * Downloads the dashboard content as PDF using html2pdf.js
 */
export const downloadDashboardAsPDF = async (organizationName?: string, pan?: string) => {
  const timestamp = new Date().toISOString().slice(0, 10)
  const orgName = organizationName?.replace(/[^a-zA-Z0-9]/g, '_') || 'Dashboard'
  const filename = `${orgName}_${pan || 'Export'}_${timestamp}.pdf`

  // Get the dashboard element
  const element = document.getElementById('ngo-diligence-content')
  if (!element) {
    console.error('Dashboard content not found')
    return
  }

  // Dynamic import of html2pdf
  const html2pdf = (await import('html2pdf.js')).default

  // Configure PDF options
  const options = {
    margin: 0.5,
    filename: filename,
    image: { type: 'jpeg' as const, quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      letterRendering: true,
      allowTaint: false
    },
    jsPDF: {
      unit: 'in' as const,
      format: 'a4' as const,
      orientation: 'portrait' as const
    }
  }

  // Clone element and remove excluded elements
  const clonedElement = element.cloneNode(true) as HTMLElement

  // Remove interactive elements
  const excludeSelectors = [
    '[data-exclude-from-export="true"]',
    'button'
  ]

  excludeSelectors.forEach(selector => {
    const elementsToRemove = clonedElement.querySelectorAll(selector)
    elementsToRemove.forEach(el => el.remove())
  })

  // Create a wrapper with PDF-safe styles (including text color fixes)
  const pdfWrapper = document.createElement('div')
  pdfWrapper.innerHTML = `
		<style>
			/* PDF-specific text color fixes */
			body {
				background: white !important;
				color: #1f2937 !important; /* Dark gray for readability */
				font-family: Arial, sans-serif !important;
			}
			
			/* Ensure all text is dark and readable */
			*, *::before, *::after {
				color: inherit !important;
			}
			
			/* Override light text colors */
			.text-gray-400, .text-gray-500 {
				color: #6b7280 !important;
			}
			
			/* Preserve table styling but ensure readability */
			table {
				background: inherit !important;
			}
			
			.bg-gray-800 {
				background-color: #1f2937 !important;
			}
			
			.bg-gray-700 {
				background-color: #374151 !important;
			}
			
			/* Remove problematic effects for PDF */
			* {
				backdrop-filter: none !important;
				filter: none !important;
				transform: none !important;
				transition: none !important;
				animation: none !important;
			}

			/* PDF table overflow fixes */
			table {
				table-layout: fixed !important;
				width: 100% !important;
				word-wrap: break-word !important;
				font-size: 10px !important;
			}

			td, th {
				word-wrap: break-word !important;
				word-break: break-word !important;
				overflow-wrap: break-word !important;
				hyphens: auto !important;
				white-space: normal !important;
				max-width: 0 !important;
				padding: 6px 3px !important;
			}

			.overflow-x-auto {
				overflow: visible !important;
			}

			/* Make all cards full width in PDF */
			.grid {
				display: grid !important;
				grid-template-columns: 1fr !important;
			}

			.grid > * {
				width: 100% !important;
				max-width: 100% !important;
			}

			.lg\\:grid-cols-2 {
				grid-template-columns: 1fr !important;
			}
		</style>
	`
  pdfWrapper.appendChild(clonedElement)

  // Generate PDF
  try {
    await html2pdf().set(options).from(pdfWrapper).save()
  } catch (error) {
    console.error('html2pdf failed, trying alternative method:', error)
    try {
      // Try alternative PDF generation method
      await downloadDashboardAsPDFAlternative(organizationName, pan)
    } catch (altError) {
      console.error('Alternative PDF generation also failed:', altError)
      // Final fallback to HTML download
      downloadDashboard(organizationName, pan)
      throw new Error('PDF generation failed with both methods')
    }
  }
}

/**
 * Alternative PDF generation using jsPDF + html2canvas (better modern CSS support)
 */
export const downloadDashboardAsPDFAlternative = async (organizationName?: string, pan?: string) => {
  const timestamp = new Date().toISOString().slice(0, 10)
  const orgName = organizationName?.replace(/[^a-zA-Z0-9]/g, '_') || 'Dashboard'
  const filename = `${orgName}_${pan || 'Export'}_${timestamp}.pdf`

  // Get the dashboard element
  const element = document.getElementById('ngo-diligence-content')
  if (!element) {
    console.error('Dashboard content not found')
    return
  }

  try {
    // Dynamic imports
    const html2canvas = (await import('html2canvas')).default
    const jsPDFModule = await import('jspdf')
    const jsPDF = jsPDFModule.default

    // Clone and prepare element
    const clonedElement = element.cloneNode(true) as HTMLElement

    // Remove excluded elements
    const excludeSelectors = [
      '[data-exclude-from-export="true"]',
      'button'
    ]

    excludeSelectors.forEach(selector => {
      const elementsToRemove = clonedElement.querySelectorAll(selector)
      elementsToRemove.forEach(el => el.remove())
    })

    // Create wrapper with proper styling for PDF
    const pdfWrapper = document.createElement('div')
    pdfWrapper.innerHTML = `
			<style>
				/* PDF-specific text color fixes */
				body {
					background: white !important;
					color: #1f2937 !important;
					font-family: Arial, sans-serif !important;
				}
				
				*, *::before, *::after {
					color: inherit !important;
				}
				
				.text-gray-400, .text-gray-500 {
					color: #6b7280 !important;
				}
				
				/* Preserve table styling but ensure readability */
				table {
					background: inherit !important;
				}
				
				.bg-gray-800 {
					background-color: #1f2937 !important;
				}
				
				.bg-gray-700 {
					background-color: #374151 !important;
				}

				/* Make all cards full width in PDF */
				.grid {
					display: grid !important;
					grid-template-columns: 1fr !important;
				}

				.grid > * {
					width: 100% !important;
					max-width: 100% !important;
				}

				.lg\\:grid-cols-2 {
					grid-template-columns: 1fr !important;
				}
			</style>
		`
    pdfWrapper.appendChild(clonedElement)

    // Temporarily add to DOM for rendering
    pdfWrapper.style.position = 'absolute'
    pdfWrapper.style.left = '-9999px'
    pdfWrapper.style.top = '0'
    pdfWrapper.style.width = '1200px'
    pdfWrapper.style.backgroundColor = '#ffffff'
    document.body.appendChild(pdfWrapper)

    // Convert to canvas
    const canvas = await html2canvas(pdfWrapper, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      removeContainer: true
    } as any)

    // Remove temporary container
    document.body.removeChild(pdfWrapper)

    // Create PDF
    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF('p', 'mm', 'a4')

    const imgWidth = 210 // A4 width in mm
    const pageHeight = 295 // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width
    let heightLeft = imgHeight
    let position = 0

    // Add first page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
    heightLeft -= pageHeight

    // Add additional pages if needed
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight
      pdf.addPage()
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight
    }

    // Save PDF
    pdf.save(filename)

  } catch (error) {
    console.error('Alternative PDF generation failed:', error)
    throw error
  }
}

/**
 * Downloads a specific section of the dashboard
 */
export const downloadDashboardSection = (
  sectionId: string,
  sectionName: string,
  organizationName?: string,
  pan?: string
) => {
  const timestamp = new Date().toISOString().slice(0, 10)
  const orgName = organizationName?.replace(/[^a-zA-Z0-9]/g, '_') || 'Dashboard'
  const filename = `${orgName}_${sectionName}_${pan || 'Export'}_${timestamp}.html`

  downloadElementAsHTML(sectionId, {
    filename,
    includeStyles: true,
    excludeSelectors: [
      '.download-button',
      '[data-exclude-from-export="true"]'
    ]
  })
}